import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./voice-input.scss";
import { errorNoti } from "../../../base/BasicNotifications";
import { StandardTooltip } from "../StandardTooltip/StandardTooltip";
import { SkeletonLoadingAnimation } from "../../../base/LoadingAnimation";
import { ConditionalRender } from "../ConditionalRender/ConditionalRender";
import { ProjectAcceptFiles } from "../../../data/config";

export const isFileAllowed = (file, acceptPatterns) => {
  for (let i = 0; i < acceptPatterns.length; i++) {
    if (matchesPattern(file, acceptPatterns[i])) return true;
  }
  return false;
};

export const parseAccept = (acceptStr) =>
  (acceptStr || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

export const matchesPattern = (file, pattern) => {
  if (!file) return false;
  const lowerPattern = pattern.toLowerCase();

  if (lowerPattern.startsWith(".")) {
    const ext = file.name.split(".").pop() || "";
    return `.${ext.toLowerCase()}` === lowerPattern;
  }

  if (lowerPattern.endsWith("/*")) {
    const prefix = lowerPattern.replace("/*", "");
    return file.type.toLowerCase().startsWith(prefix + "/");
  }

  if (file.type) {
    return file.type.toLowerCase() === lowerPattern;
  }

  if (lowerPattern.includes("/")) {
    const maybeExt = lowerPattern.split("/").pop();
    const ext = file.name.split(".").pop() || "";
    return maybeExt === ext.toLowerCase();
  }

  return false;
};

export const VoiceInput = ({
  value,
  onChange,
  placeholder = "Describe what you want to work on...",
  disabled = false,
  onSend,
  onPlus,
  onClear,
  onMicResult,
  // micLang = "gu-IN",
  micLang = "en-US",
  // micLang = "hi-IN",
  commitMode = "replace", // "append" | "replace"
  fileAccept = ProjectAcceptFiles,
  fileMultiple = true,
  className,
  showMic,
  isLoading = false,
  onCancel,
  files,
  onRemoveFile,
  isFileUploading = false,
  handleRemoveFile,
  // Optional suggested prompts shown as chips INSIDE the input box, above
  // the row, while the draft is empty (same as the SME VoiceInput). Clicking
  // one fills the input and focuses it. Default [] = nothing rendered, so
  // every existing caller is unchanged.
  quickStarters = [],
}) => {
  const [focused, setFocused] = useState(false);
  const [listening, setListening] = useState(false);
  const listeningRef = useRef(listening);
  const [internalValue, setInternalValue] = useState(value || "");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [pendingTranscript, setPendingTranscript] = useState("");
  const [micHover, setMicHover] = useState(false);
  const [filesList, setFilesList] = useState([]);

  const BAR_COUNT = 90;
  const [levels, setLevels] = useState(
    Array.from({ length: BAR_COUNT }, () => 6),
  );
  const recognitionRef = useRef(null);

  // audio refs
  const audioStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const rafRef = useRef(null);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // keep ref synced with state so closures can read intended value
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  // Sync prop -> internal
  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  const startAudioAnalysis = async () => {
    try {
      // if already have stream, keep reusing it
      if (audioStreamRef.current) return;

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia)
        return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      const loop = () => {
        try {
          analyser.getByteTimeDomainData(dataArray);
        } catch (e) {
          return;
        }

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / bufferLength);
        const amplitude = Math.min(1, Math.max(0.02, rms * 3.2));

        const newLevels = Array.from({ length: BAR_COUNT }).map((_, i) => {
          const phase = Date.now() / 100 + i;
          const jitter = 0.5 + 0.8 * Math.abs(Math.sin(phase));
          const h = Math.round(amplitude * 100 * jitter);
          return Math.max(6, Math.min(100, h));
        });
        setLevels(newLevels);

        rafRef.current = requestAnimationFrame(loop);
      };

      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      errorNoti("Microphone access denied or unavailable");
    }
  };

  const stopAudioAnalysis = () => {
    try {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch (e) {}
        analyserRef.current = null;
      }
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch (e) {}
        audioCtxRef.current = null;
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
      }
      setLevels(Array.from({ length: BAR_COUNT }, () => 6));
    } catch (e) {
      // noop
    }
  };

  const fileKey = (f) =>
    `${f?.name || ""}_${f?.size || ""}_${f?.lastModified || ""}`;

  const applyFiles = (files) => {
    let nextFiles;
    if (fileMultiple) {
      nextFiles = [...filesList, ...files];
    } else {
      nextFiles = [files[0]];
    }
    setFilesList(nextFiles);
    onPlus?.(files);
  };

  const removeFile = (index) => {
    const next = filesList.filter((_, i) => i !== index);
    const nextFiles = files.filter((_, i) => i !== index);
    handleRemoveFile(nextFiles);
    setFilesList(next);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const acceptPatterns = parseAccept(fileAccept);
    if (!acceptPatterns.length) {
      applyFiles(files);
      e.target.value = "";
      return;
    }
    const invalid = files.filter((f) => !isFileAllowed(f, acceptPatterns));
    if (invalid.length) {
      errorNoti("Please enter valid file.");
      e.target.value = "";
      return;
    }
    applyFiles(files);
    e.target.value = "";
  };

  // ------------------------------
  // SpeechRecognition setup with auto-restart while listeningRef === true
  // ------------------------------
  useEffect(() => {
    const SpeechRecognition =
      typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SpeechRecognition) return;

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = micLang;

    rec.onresult = (event) => {
      let interim = "";
      let finals = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          finals += res[0].transcript;
        } else {
          interim += res[0].transcript;
        }
      }

      // keep interim updated
      if (interim) setInterimTranscript(interim.trim());
      else setInterimTranscript("");

      // append final transcripts to pending (don't stop recognition)
      if (finals) {
        setInterimTranscript("");
        setPendingTranscript(
          (prev) => `${prev}${prev ? " " : ""}${finals.trim()}`,
        );
        onMicResult?.(finals);
      }
    };
    rec.onend = async () => {
      // stop audio analyser because stream might be closed
      stopAudioAnalysis();

      // if user still wants to listen, try to restart (small backoff)
      if (listeningRef.current) {
        // attempt to re-acquire audio and restart recognition
        try {
          // ensure audio analysis is running before starting recognition
          await startAudioAnalysis();
        } catch (e) {
          // ignore - startAudioAnalysis handles errorNoti
        }

        // small timeout to avoid tight-loop restarts
        setTimeout(() => {
          try {
            rec.start();
          } catch (e) {
            // starting may throw, ignore and rely on future onend attempts
          }
        }, 250);
      } else {
        // truly stopped by user
        setListening(false);
      }
    };

    rec.onerror = (err) => {
      // stop analyzer and set listening false; onend will handle restart logic if needed
      stopAudioAnalysis();
      // keep listening state true/false as-is; but set to false to reflect error
      setListening(false);
      listeningRef.current = false;
      // optionally show error
      // errorNoti(err?.message || "Speech recognition error");
    };

    recognitionRef.current = rec;

    // cleanup on unmount
    return () => {
      try {
        rec.onresult = null;
        rec.onend = null;
        rec.onerror = null;
        rec.stop();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micLang, onMicResult]);

  // Auto-resize helper
  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    if (!displayValue) {
      el.style.height = "auto";
      return;
    }
    el.style.height = "auto";
    const computed = window.getComputedStyle(el);
    let lineHeight = parseFloat(computed.lineHeight);
    if (
      Number.isNaN(lineHeight) ||
      !lineHeight ||
      computed.lineHeight === "normal"
    ) {
      const fontSize = parseFloat(computed.fontSize) || 14;
      lineHeight = fontSize * 1.35;
    }
    const paddingY =
      parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom);
    const maxHeight = lineHeight * 5 + paddingY;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [internalValue, interimTranscript, pendingTranscript, listening]);

  const startListening = async () => {
    if (!recognitionRef.current) return;
    try {
      await startAudioAnalysis();
      listeningRef.current = true;
      recognitionRef.current.start();
      setPendingTranscript("");
      setInterimTranscript("");
      setListening(true);
    } catch (e) {
      setListening(false);
      listeningRef.current = false;
      stopAudioAnalysis();
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch (e) {}
    listeningRef.current = false;
    setListening(false);
  };

  const handleMicClick = () => {
    if (disabled || isFileUploading) return;
    if (listening) {
      // user explicitly stops => reject pending transcript (as earlier behavior)
      stopListening();
      setPendingTranscript("");
      setInterimTranscript("");
    } else {
      startListening();
    }
  };

  const handleSend = () => {
    if (disabled || isFileUploading) return;
    onSend?.(internalValue);
    handleClear();
  };

  const handlePlus = () => {
    if (disabled || isFileUploading) return;
    try {
      fileInputRef.current?.click();
    } catch (e) {}
  };

  const handleClear = () => {
    if (disabled || isFileUploading) return;
    setInternalValue("");
    setFilesList([]);
    onChange?.("");
    onClear?.();
  };

  const handleConfirmPending = () => {
    if (disabled || isFileUploading) return;
    const text = (pendingTranscript || interimTranscript).trim();
    if (!text) {
      stopListening();
      setPendingTranscript("");
      setInterimTranscript("");
      return;
    }
    const next =
      commitMode === "replace"
        ? text
        : `${internalValue}${internalValue ? " " : ""}${text}`;
    setInternalValue(next);
    onChange?.(next);
    setPendingTranscript("");
    setInterimTranscript("");
    stopListening();
  };

  const handleRejectPending = () => {
    if (disabled) return;
    setPendingTranscript("");
    setInterimTranscript("");
    stopListening();
  };

  const containerClass = classNames(
    "voice-input",
    {
      focus: focused,
      "dictate-active": listening,
    },
    className,
  );

  // While listening, show spoken preview inside the input
  const previewText = [pendingTranscript, interimTranscript]
    .filter(Boolean)
    .join(" ")
    .trim();
  const displayValue = listening
    ? previewText || "listening..."
    : internalValue;

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch (e) {}
      stopAudioAnalysis();
      window.clearTimeout(handleFileChange._errTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showQuickStarters =
    Array.isArray(quickStarters) &&
    quickStarters.length > 0 &&
    !listening &&
    (internalValue || "").trim().length <= 1;
  const handleQuickStarter = (text) => {
    setInternalValue(text);
    onChange?.(text);
    window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = text.length;
      try {
        el.setSelectionRange(end, end);
      } catch (e) {}
    });
  };

  return (
    <div className="voice-input-container mt-3">
      {showQuickStarters && (
        <div className="vi-quick-starters" role="list">
          <span className="vi-quick-starters__hint">Try one of these</span>
          <div className="vi-quick-starters__row">
            {quickStarters.map((text, i) => (
              <button
                key={`${text}-${i}`}
                type="button"
                role="listitem"
                className="vi-quick-starter"
                onClick={() => handleQuickStarter(text)}
                title={text}
                disabled={disabled}
              >
                <span className="vi-quick-starter__label">{text}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {(Array.isArray(filesList) ? filesList.length > 0 : files.length > 0) && (
        <>
          <ConditionalRender condition={isFileUploading}>
            <div className="d-flex file-loading">
              {(Array.isArray(filesList) ? filesList : files).map((f, idx) => {
                return (
                  <div className="" key={`${f.name}_${idx}`}>
                    <SkeletonLoadingAnimation
                      noPadding={true}
                      height={40.5}
                      width={160}
                      count={1}
                    />
                  </div>
                );
              })}
            </div>
          </ConditionalRender>
          <div className="files-row">
            <div className="files-inner">
              <ConditionalRender condition={!isFileUploading}>
                {(Array.isArray(files) ? files : filesList).map((f, idx) => {
                  return (
                    <div className="file-chip" key={`${f.name}_${idx}`}>
                      <div className="file-icon" aria-hidden>
                        <img src="/assets/images/file-primary.svg" alt="" />
                      </div>
                      <div className="file-name" title={f.name}>
                        {f.name}
                      </div>

                      <button
                        type="button"
                        className="file-remove ms-auto"
                        onClick={() => {
                          // if (Array.isArray(files)) {
                          //   onRemoveFile?.(f, idx);
                          // } else {
                          removeFile(idx);
                          // }
                        }}
                        aria-label="Remove file"
                      >
                        <img src="/assets/images/cross-grey.svg" alt="remove" />
                      </button>
                    </div>
                  );
                })}
              </ConditionalRender>
            </div>
          </div>
        </>
      )}
      <div className={containerClass}>
        <input
          ref={fileInputRef}
          type="file"
          accept={fileAccept}
          className="d-none"
          multiple={fileMultiple}
          onChange={handleFileChange}
        />

        <div className="h-100 add-btn">
          <StandardTooltip
            tooltTipContent={`Add Photos and Files`}
            placement="top"
          >
            <button
              type="button"
              className="icon-btn left "
              aria-label="Add"
              onClick={handlePlus}
              disabled={disabled}
            >
              <img src="/assets/images/plus-dark.svg" alt="add" />
            </button>
          </StandardTooltip>
        </div>

        <div className="main-area">
          <textarea
            className={classNames("text-input", {
              "listening-empty": listening && !previewText,
              "light-text": listening,
            })}
            ref={textareaRef}
            value={displayValue}
            placeholder={listening ? "" : placeholder}
            onChange={(e) => {
              if (listening) return;
              setInternalValue(e.target.value);
              onChange?.(e.target.value);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            // disabled={disabled}
            readOnly={listening}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            spellCheck={false}
          />
          {/* {listening && (
            <div className="wave-overlay" aria-hidden>
              <div className="wave-inner">
                {levels.map((h, i) => (
                  <span
                    key={i}
                    className="wave-bar"
                    style={{
                      height: `${h}%`,
                      transition: "height 120ms linear",
                    }}
                  />
                ))}
              </div>
            </div>
          )} */}
        </div>
        <div className="actions">
          <ConditionalRender condition={showMic}>
            {listening ? (
              <>
                <button
                  type="button"
                  className="icon-btn reject"
                  aria-label="Reject transcript"
                  onClick={handleRejectPending}
                  disabled={disabled}
                >
                  <img src="/assets/images/cross-grey.svg" alt="reject" />
                </button>

                <button
                  type="button"
                  className="icon-btn confirm"
                  aria-label="Confirm transcript"
                  onClick={handleConfirmPending}
                  disabled={disabled}
                >
                  <img src="/assets/images/true-primary.svg" alt="confirm" />
                </button>
              </>
            ) : (
              <button
                type="button"
                className={classNames("icon-btn mic", { active: listening })}
                aria-label={listening ? "Stop dictation" : "Start dictation"}
                onClick={handleMicClick}
                disabled={disabled}
                onMouseEnter={() => setMicHover(true)}
                onMouseLeave={() => setMicHover(false)}
              >
                <img
                  src={
                    listening || micHover
                      ? "/assets/images/mic-primary.svg"
                      : "/assets/images/mic-dark.svg"
                  }
                  alt="mic"
                  className="mic-img"
                  draggable={false}
                />
              </button>
            )}
          </ConditionalRender>

          <ConditionalRender condition={!isLoading}>
            <button
              type="button"
              className={`icon-btn send ${
                disabled ||
                (listening && (pendingTranscript || interimTranscript)) ||
                !internalValue ||
                isFileUploading
                  ? ""
                  : "cursor-pointer"
              }`}
              aria-label="Send"
              onClick={handleSend}
              disabled={
                disabled ||
                (listening && (pendingTranscript || interimTranscript)) ||
                !internalValue ||
                isFileUploading
              }
            >
              {disabled ||
              (listening && (pendingTranscript || interimTranscript)) ||
              !internalValue ||
              isFileUploading ? (
                <img src="/assets/images/right-arrow-grey.svg" alt="Send" />
              ) : (
                <img
                  src="/assets/images/right-arrow-white-wider.svg"
                  alt="Send"
                />
              )}
            </button>
          </ConditionalRender>

          <ConditionalRender condition={isLoading}>
            <button
              type="button"
              className="icon-btn send"
              aria-label="Send"
              onClick={() => {
                onCancel?.();
              }}
            >
              <img src="/assets/images/pause-white.svg" alt="Send" />
            </button>
          </ConditionalRender>
        </div>
      </div>
    </div>
  );
};

VoiceInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onSend: PropTypes.func,
  onPlus: PropTypes.func,
  onClear: PropTypes.func,
  onCancel: PropTypes.func,
  onMicResult: PropTypes.func,
  micLang: PropTypes.string,
  commitMode: PropTypes.oneOf(["append", "replace"]),
  fileAccept: PropTypes.string,
  fileMultiple: PropTypes.bool,
  className: PropTypes.string,
  showMic: PropTypes.bool,
  isLoading: PropTypes.bool,
  quickStarters: PropTypes.arrayOf(PropTypes.string),
};

export default VoiceInput;
